import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Icon } from "@/lib/icons";
import NeedsAttention from "@/components/NeedsAttention";
import GardenSnapshot from "@/components/GardenSnapshot";
import MyMessages from "@/components/MyMessages";
import MemberSubmit from "@/components/MemberSubmit";
import WaysToGive from "@/components/giving/WaysToGive";

const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : "");

export default function BoardDashboard({ me, onNavigate }) {
  const first = (me.user.name || "there").split(" ")[0];
  const [data, setData] = useState(undefined);

  const load = useCallback(async () => {
    const r = await api.get("/board-dashboard");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const go = (id) => onNavigate && onNavigate(id);

  return (
    <div className="space-y-5" data-testid="board-home">
      <div>
        <h1 className="font-zilla text-3xl font-semibold">Good day, {first}</h1>
        <p className="text-[16px] text-ink/70">{me.user.board_position || me.user.seat ? `${[me.user.board_position, me.user.seat].filter(Boolean).join(" · ")} · Board` : "Board member"}</p>
      </div>

      <MemberSubmit />

      {data === undefined ? (
        <div className="text-ink/60 py-6">Loading…</div>
      ) : data.show ? (
        <div className="space-y-4" data-testid="board-governance-card">
          {/* Next meeting */}
          {data.next_meeting && (
            <div className="rounded-2xl p-6 bg-ink text-cream" data-testid="board-next-meeting">
              <div className="text-[12px] uppercase tracking-widest text-gold font-bold">Next board meeting</div>
              <div className="font-zilla text-2xl font-semibold mt-2">{data.next_meeting.title || fmt(data.next_meeting.meeting_date)}</div>
              {data.next_meeting.title && <div className="text-cream/75 text-[15px]">{fmt(data.next_meeting.meeting_date)}</div>}
              <button
                data-testid="board-open-meeting"
                onClick={() => go("board_governance")}
                className="mt-4 bg-olive text-ink font-bold rounded-lg px-4 py-2.5 min-h-[44px] text-[15px]"
              >
                Open the workspace
              </button>
            </div>
          )}

          {/* Live counts */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              data-testid="board-votes-tile"
              onClick={() => go("votes_approvals")}
              className="text-left bg-cream border border-ink/15 rounded-xl p-4 hover:border-maroon/50 transition-colors"
            >
              <Icon name="CheckSquare" className="w-5 h-5 text-maroon mb-2" />
              <div className="text-[13px] uppercase tracking-wide text-ink/55 font-semibold">Your vote is needed</div>
              <div className="font-zilla text-2xl font-semibold">{data.pending_votes}</div>
            </button>
            <button
              data-testid="board-tasks-tile"
              onClick={() => go("my_tasks")}
              className="text-left bg-cream border border-ink/15 rounded-xl p-4 hover:border-maroon/50 transition-colors"
            >
              <Icon name="ListTodo" className="w-5 h-5 text-maroon mb-2" />
              <div className="text-[13px] uppercase tracking-wide text-ink/55 font-semibold">Tasks assigned to you</div>
              <div className="font-zilla text-2xl font-semibold">{data.open_tasks}</div>
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-cream border border-ink/15 rounded-xl p-6 text-center text-ink/60" data-testid="board-all-clear">
          <Icon name="Check" className="w-6 h-6 text-olive mx-auto mb-2" />
          You're all caught up — no meetings scheduled, votes, or tasks awaiting you.
        </div>
      )}

      <NeedsAttention showGovernance={false} onNavigate={go} />
      <WaysToGive prefill={{ name: me.user.name, email: me.user.email }} onSeeAll={() => go("give")} />
      <div className="mt-4"><GardenSnapshot onNavigate={go} /></div>
      <MyMessages />

      <div className="bg-cream border border-ink/15 rounded-xl p-5">
        <h2 className="font-zilla text-xl font-semibold mb-1">Your own membership</h2>
        <p className="text-ink/60 text-[15px]">
          Your board access sits on top of your member account — open <button data-testid="board-goto-account" onClick={() => go("my_membership")} className="text-dark-olive underline font-semibold">My Membership</button> for your card, points, dues, and event history.
        </p>
      </div>
    </div>
  );
}
